import { keepPreviousData, useQueryClient } from '@tanstack/react-query'
import { ColumnDef } from '@tanstack/react-table'
import {
  ActionsDropdown,
  createEditAction,
} from 'components/Buttons/ActionsDropdown'
import { OverflownText } from 'components/OverflownText'
import { TextPill } from 'components/FbUI/TextPill'
import { FbModal } from 'components/UI/Modal'
import FilterModal from 'components/Filters/components/FilterModal'
import { filterStoreRepo } from 'components/Filters/FilterStore'
import { useAuth } from 'context/authentication/useAuth'
import { InviteUserModal } from 'features/users/InviteUserModal'
import { useRSMEligibleUsers } from 'hooks/useRSMEligibleUsers'
import { User, USER_TYPE_LIMITED } from 'models/user'
import Select from 'components/FormUtils/Select'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { PiMagnifyingGlass, PiPlus, PiWarning, PiInfo } from 'react-icons/pi'
import { toast } from 'react-toastify'
import { useDebounce } from 'use-debounce'
import useApiService from 'services/api'
import { useListUserTypes, useUpdateUserType, useUsers } from 'services/users'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { ClientDataTable } from '../../../components/ClientDataTable/ClientDataTable'
import { DataTableContainer } from '../../../components/DataTable/UI'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { UserFilters } from './UserFilters'
import { UserRecommendationPreferences } from './UserRecommendationPreferences'

interface UserTableFormValues {
  user_type: string
  rsm_assignment: number | null
}

export function UserTable() {
  const api = useApiService()
  const { user, isAdmin } = useAuth()
  const { enableUserTypes } = useFeatureFlagService()
  const queryClient = useQueryClient()

  const filterStore = useMemo(() => filterStoreRepo.getStore('USERS_TABLE'), [])
  const { valueFilters, search, setSearch } = filterStore()

  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { control, reset, watch } = useForm<UserTableFormValues>({
    defaultValues: { user_type: '', rsm_assignment: null },
  })

  const selectedType = watch('user_type')
  const selectedRSM = watch('rsm_assignment')

  const userTypeFilter = valueFilters['user_type']?.map((v) => v.value)
  const isActiveFilter = valueFilters['is_active']?.[0]?.value as
    | 'true'
    | 'false'
    | undefined
  const searchValue = search['search']?.value ?? ''
  const [searchInput, setSearchInput] = useState(searchValue)
  const [debouncedSearchInput] = useDebounce(searchInput, 300)

  useEffect(() => {
    if (debouncedSearchInput !== searchValue) {
      setSearch('search', debouncedSearchInput, 'Search')
    }
  }, [debouncedSearchInput, searchValue, setSearch])

  const {
    data: _data,
    isFetching,
    refetch,
  } = useUsers(
    {
      user_type: userTypeFilter?.length ? userTypeFilter : undefined,
      is_active: isActiveFilter,
      search: searchValue || undefined,
    },
    { query: { enabled: true, placeholderData: keepPreviousData } }
  )
  const data = _data as User[] | undefined

  // Unfiltered list backs the InviteUserModal's duplicate-email check.
  // Filtered `data` would let an invite slip through when the matching user
  // is hidden by the active filters.
  const { data: _allUsers } = useUsers(undefined, { query: { enabled: true } })
  const allUsers = _allUsers as User[] | undefined

  const { data: userTypes } = useListUserTypes({
    query: { enabled: enableUserTypes && isAdmin },
  })

  const updateUserType = useUpdateUserType()

  const { rsmOptions } = useRSMEligibleUsers(
    enableUserTypes && isAdmin,
    selectedUser?.id
  )

  const { data: companyData } = api.useGetCompanyDetails()

  const handleEdit = useCallback(
    (targetUser: User) => {
      setSelectedUser(targetUser)
      reset({
        user_type: targetUser.user_type ?? 'Standard',
        rsm_assignment: targetUser.rsm_assignment ?? null,
      })
    },
    [reset]
  )

  const isLimitedType = selectedType === USER_TYPE_LIMITED
  const isUpgrading =
    selectedUser?.user_type === USER_TYPE_LIMITED && !isLimitedType
  const isDowngrading =
    selectedUser?.user_type !== USER_TYPE_LIMITED && isLimitedType
  const companyBucket = companyData?.seat_buckets?.find((b) =>
    b.user_types.some((t) => t === 'Admin' || t === 'Standard')
  )
  const maxSeatsReached = companyBucket
    ? companyBucket.active >= companyBucket.limit
    : false

  const blockAccept = useMemo(() => {
    if (!selectedType) return true
    if (isLimitedType && !selectedRSM) return true
    if (isUpgrading && maxSeatsReached) return true
    // Block if nothing changed
    const sameType = selectedType === (selectedUser?.user_type ?? 'Standard')
    if (isLimitedType) {
      return sameType && selectedRSM === (selectedUser?.rsm_assignment ?? null)
    }
    return sameType
  }, [
    selectedType,
    isLimitedType,
    selectedRSM,
    isUpgrading,
    maxSeatsReached,
    selectedUser,
  ])

  const assignedBrokersCount = selectedUser?.assigned_brokers_count ?? 0

  const handleSubmit = useCallback(async () => {
    if (!selectedUser || !selectedType) return
    setIsSubmitting(true)
    try {
      await updateUserType.mutateAsync({
        user_id: selectedUser.id,
        data: {
          user_type: selectedType,
          rsm_assignment_id: isLimitedType ? selectedRSM : null,
        },
      })
      toast.success('User type updated successfully')
      await refetch()
      queryClient.invalidateQueries({ queryKey: ['company-details'] })
      queryClient.invalidateQueries({ queryKey: ['rsm-eligible-users'] })
      setSelectedUser(null)
    } catch {
      toast.error('Failed to update user type')
    } finally {
      setIsSubmitting(false)
    }
  }, [
    selectedUser,
    selectedType,
    selectedRSM,
    isLimitedType,
    updateUserType,
    refetch,
    queryClient,
  ])

  const columns = useMemo<ColumnDef<User, any>[]>(() => {
    const cols: ColumnDef<User, any>[] = [
      {
        accessorKey: 'first_name',
        header: 'First Name',
        size: 200,
        meta: { isFirstColumn: true },
      },
      {
        accessorKey: 'last_name',
        header: 'Last Name',
        size: 200,
      },
      {
        accessorKey: 'email',
        header: 'Email',
        size: 260,
        cell: (info) => (
          <OverflownText>{info.getValue() as string}</OverflownText>
        ),
      },
      {
        accessorKey: 'is_active',
        header: 'Status',
        size: 140,
        cell: (info) => {
          if (info.getValue()) {
            return (
              <TextPill textColor="#15803d" backgroundColor="#dcfce7">
                Active
              </TextPill>
            )
          }
          return (
            <TextPill textColor="#374151" backgroundColor="#f3f4f6">
              Inactive
            </TextPill>
          )
        },
      },
    ]

    if (enableUserTypes) {
      cols.push({
        accessorKey: 'user_type',
        header: 'User Type',
        size: 140,
        cell: (info) => {
          const value = (info.getValue() as string) ?? 'Standard'
          return <span>{value}</span>
        },
      })

      cols.push({
        id: 'rsm_assignment',
        header: 'Sample Reviewer',
        size: 200,
        cell: ({ row }) => {
          const u = row.original
          if (u.user_type !== USER_TYPE_LIMITED) return '-'
          if (u.rsm_assignment_name) {
            return <span>{u.rsm_assignment_name}</span>
          }
          return (
            <span className="flex items-center gap-1 text-gray-500">
              <PiWarning className="h-4 w-4 text-warning" />
              Unassigned
            </span>
          )
        },
      })

      if (isAdmin) {
        cols.push({
          id: 'actions',
          header: () => <div className="w-full text-center">Actions</div>,
          size: 70,
          meta: {
            isEditable: false,
            headerDisableOrdering: true,
            isLastColumn: true,
          },
          cell: ({ row }) => {
            const targetUser = row.original
            const isSelf = targetUser.id === user?.id
            return (
              <ActionsDropdown
                className="justify-center"
                actions={[
                  {
                    ...createEditAction(
                      () => handleEdit(targetUser),
                      'Edit User Type'
                    ),
                    disabled: isSelf,
                    disabledReason: isSelf
                      ? 'You cannot change your own user type'
                      : undefined,
                  },
                ]}
              />
            )
          },
        })
      }
    }

    return cols
  }, [enableUserTypes, isAdmin, user?.id, handleEdit])

  return (
    <TableLinkStyleProvider variant="plain">
      <div className="flex flex-col w-full flex-1 min-h-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
        <DataTableContainer className="h-full">
          <ClientDataTable
            tableHeader={
              <div
                data-table-toolbar
                className="flex flex-row items-center justify-between gap-3 px-4 py-3"
              >
                <div className="relative w-full max-w-md">
                  <PiMagnifyingGlass
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
                  />
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search by name or email..."
                    className="w-full h-9 pl-9 pr-3 text-sm bg-white border border-gray-300 rounded-lg text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-brand-active focus:shadow-[0_0_0_4px_rgba(8,116,67,0.2)]"
                  />
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <FilterModal
                    store={filterStore}
                    identifier="USERS_TABLE"
                    disableSavedFilters
                  >
                    <UserFilters store={filterStore} />
                  </FilterModal>
                  <InviteUserModal
                    existingUsers={allUsers ?? []}
                    triggerLabel="Invite User"
                    triggerIcon={<PiPlus size={16} />}
                    triggerClassName="!bg-brand-bright !text-white hover:!bg-brand-active shrink-0"
                  />
                </div>
              </div>
            }
            height={'100%'}
            stickyLastColumn={enableUserTypes && isAdmin}
            loading={isFetching}
            data={data ?? []}
            columns={columns}
            enableColumnResizing
            rowHeight={44}
            headerHeight={40}
            tableKey="settings-users-table"
          />
        </DataTableContainer>
      </div>

      <FbModal
        open={!!selectedUser}
        onOpenChange={(open) => {
          if (!open) setSelectedUser(null)
        }}
        title={
          selectedUser
            ? `Select what user type to change ${selectedUser.first_name} ${selectedUser.last_name}`
            : 'Edit User Type'
        }
        loading={isSubmitting}
        blockAccept={blockAccept}
        onAccept={handleSubmit}
        acceptButtonText="Update"
        size="md"
      >
        <div className="flex flex-col gap-4">
          <Select
            control={control}
            name="user_type"
            label="User Type"
            options={userTypes ?? []}
            isClearable={false}
            disabled={isSubmitting}
          />

          {isUpgrading &&
            (maxSeatsReached ? (
              <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm">
                <PiWarning className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                <p className="font-medium text-red-800">
                  Max seats reached. Please contact First Bite to add additional
                  seats.
                </p>
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-lg border border-sky-300 bg-sky-50 p-3 text-sm">
                <PiInfo className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
                <p className="text-sky-800">
                  Upgrading this user will count as a seat. You are currently
                  using {companyBucket?.active ?? 0}/{companyBucket?.limit ?? 0}{' '}
                  seats.
                </p>
              </div>
            ))}

          {isDowngrading && (
            <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-sm">
              <div className="flex items-start gap-2">
                <PiWarning className="mt-0.5 h-4 w-4 shrink-0 text-yellow-600" />
                <div>
                  <p className="font-medium text-yellow-800">
                    Downgrading this user will restrict access to most of First
                    Bite.
                  </p>
                  {assignedBrokersCount > 0 && (
                    <p className="mt-1.5 text-yellow-700">
                      {assignedBrokersCount} broker
                      {assignedBrokersCount > 1 ? 's' : ''} currently assigned
                      to this user as their sample reviewer will be unassigned
                      and blocked from submitting sample requests until
                      reassigned.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {isLimitedType && (
            <Select
              control={control}
              name="rsm_assignment"
              label="Assigned Sample Reviewer"
              options={rsmOptions}
              isClearable={false}
              disabled={isSubmitting}
              placeholder="Select a sample reviewer..."
            />
          )}

          {selectedUser && (
            <UserRecommendationPreferences userId={selectedUser.id} />
          )}
        </div>
      </FbModal>
    </TableLinkStyleProvider>
  )
}
